import { calculatePercentage } from '@/modules/shared/utils/calculate-percentage';
import {
  DATE_FORMAT_DAILY,
  DATE_FORMAT_MONTHLY,
  DATE_FORMAT_WEEKLY,
  buildDynamicDateRange,
  buildDailyData,
  buildMonthlyData,
  buildWeeklyData,
} from './dynamicBarChartDataGenerator';
import { DateRangeOptionId, DateRangeSelection } from '@builder';
import { Serie } from '@/modules/shared/types/DynamicBarChart.types';
import { Data } from '../components/ParticipantRecluitment/shared/types/PartnerConsentedParticipantData.types';

const dailyDateFormat = 'EEE MMM d yyyy';

export const formatConsentedParticipantsGraph = (
  data: Serie[],
  { endDate, selectedDateRange, startDate }: DateRangeSelection,
): Serie[] => {
  const propNamesToFormat: Array<keyof Serie> = ['value', 'converted'];

  let dateFormat: string;
  // eslint-disable-next-line no-use-before-define
  let newData: Record<string, Serie> = {};

  const sortedData: Serie[] = data.sort((a, b) => (new Date(a.groupValue) > new Date(b.groupValue) ? 1 : -1));

  if (!startDate || !endDate) {
    throw new Error('startDate or endDate must not be empty.');
  }

  switch (selectedDateRange) {
    case DateRangeOptionId.THIS_WEEK:
      // each bar per day
      dateFormat = DATE_FORMAT_DAILY;
      newData = buildDailyData(sortedData, startDate, endDate, dateFormat, propNamesToFormat);
      break;
    case DateRangeOptionId.THIS_MONTH:
    case DateRangeOptionId.LAST_MONTH:
      // each bar per week
      dateFormat = DATE_FORMAT_WEEKLY;
      newData = buildWeeklyData(sortedData, startDate, endDate, dateFormat, propNamesToFormat);

      break;
    case DateRangeOptionId.THIS_YEAR:
      // each bar per month
      dateFormat = DATE_FORMAT_MONTHLY;
      newData = buildMonthlyData(sortedData, startDate, endDate, dateFormat, propNamesToFormat);
      break;
    case DateRangeOptionId.ALL_TIME:
      // eslint-disable-next-line no-case-declarations
      const allTimeData = buildDynamicDateRange(
        sortedData,
        startDate,
        endDate,
        {
          dailyDateFormat,
        },
        propNamesToFormat,
      );
      dateFormat = allTimeData.dateFormat;
      newData = allTimeData.data;
      break;
    case DateRangeOptionId.CUSTOM:
      // eslint-disable-next-line no-case-declarations
      const customData = buildDynamicDateRange(sortedData, startDate, endDate, {}, propNamesToFormat);

      dateFormat = customData.dateFormat;
      newData = customData.data;
      break;
  }

  const formattedData = Object.values(newData);

  formattedData.forEach((item) => {
    const { converted, value } = item;
    if (converted === 0 || value === 0) return 0;
    item.convertedPercentage = calculatePercentage(item.converted, item.value);
  });

  return formattedData;
};

export const formatConsentedParticipantGraph = (
  data: Data,
  { endDate, selectedDateRange, startDate }: DateRangeSelection,
): Serie[] => {
  const propNamesToFormat: Array<keyof Serie> = ['value', 'converted'];

  // eslint-disable-next-line no-use-before-define
  let newData: Record<string, Serie> = {};

  // convert data data.groupData.dateSeries to Serie
  const dataArr = data.groupData.dateSeries.map((item) => ({
    groupValue: item.groupValue,
    value: item.consentValue,
    converted: item.conversionValue,
  }));

  const sortedData: Serie[] = dataArr.sort((a, b) => (new Date(a.groupValue) > new Date(b.groupValue) ? 1 : -1));

  if (!startDate || !endDate) {
    throw new Error('startDate or endDate must not be empty.');
  }

  switch (selectedDateRange) {
    case DateRangeOptionId.THIS_WEEK:
      // each bar per day
      newData = buildDailyData(sortedData, startDate, endDate, DATE_FORMAT_DAILY, propNamesToFormat);
      break;
    case DateRangeOptionId.CUSTOM:
      // each bar per day with month
      newData = buildDailyData(sortedData, startDate, endDate, DATE_FORMAT_DAILY, propNamesToFormat, true);
      break;
    case DateRangeOptionId.THIS_MONTH:
    case DateRangeOptionId.LAST_MONTH:
      // each bar per week
      newData = buildWeeklyData(sortedData, startDate, endDate, DATE_FORMAT_WEEKLY, propNamesToFormat);
      break;
    case DateRangeOptionId.ALL_TIME: {
      const _startDate = sortedData.length > 0 ? new Date(sortedData[0].groupValue) : startDate;
      const _endDate = sortedData.length > 0 ? new Date(sortedData[sortedData.length - 1].groupValue) : new Date();
      const allTimeData = buildDynamicDateRange(
        sortedData,
        _startDate,
        _endDate,
        {
          dailyDateFormat,
        },
        propNamesToFormat,
      );
      newData = allTimeData.data;
      break;
    }
    default:
      newData = buildMonthlyData(sortedData, startDate, endDate, DATE_FORMAT_MONTHLY, propNamesToFormat);
      break;
  }

  const formattedData = Object.values(newData);

  formattedData.forEach((item) => {
    const { converted, value } = item;
    if (converted === 0 || value === 0) return 0;
    item.convertedPercentage = calculatePercentage(item.converted, item.value);
  });

  return formattedData;
};
